/* eslint-disable max-classes-per-file */
import config from '../../../config'
import http from './http-util'
import { createSearchBody } from './request-builder/payment-engine-request-builder'
import { appendPageInfoQueryParams, appendQueryParam } from './api-util'

const prepareForRedirectionRequest = ({ customer, coCustomer }) => {
  const preferredCommunications = { customer, coCustomer }
  if (customer && customer.phoneNumber) {
    preferredCommunications.customer = {
      ...customer,
      mobile: customer.phoneNumber,
    }
  }
  if (coCustomer && coCustomer.phoneNumber) {
    preferredCommunications.coCustomer = {
      ...coCustomer,
      mobile: coCustomer.phoneNumber,
    }
  }
  return {
    preferredCommunications,
    sendLinks: true,
  }
}

class PaymentEngineApi {
  static application = class ApplicationApi {
    static create = (application, token) => {
      return http.post(
        token,
        `${config.paymentEngineServiceUrl}/applications`,
        application,
      )
    }

    static get = (id, token) => {
      return http.get(
        token,
        `${config.paymentEngineServiceUrl}/applications/${id}`,
      )
    }

    static apply = (applyRequest, token) => {
      return http.post(
        token,
        `${config.paymentEngineServiceUrl}/applications/${applyRequest.applicationId}/apply`,
        JSON.stringify(applyRequest.apply),
        config.requestTimeoutApply,
      )
    }

    static prepareForRedirection =
      (
        applicationId,
        forceElectronicSigning,
        useDigitalAgreement = false,
        preferredCommunication = {},
      ) =>
      token => {
        let queryParams = ''
        queryParams = appendQueryParam(
          queryParams,
          'forceElectronicSigning',
          forceElectronicSigning,
        )
        queryParams = appendQueryParam(
          queryParams,
          'useDigitalAgreementSigning',
          useDigitalAgreement,
        )
        const body = prepareForRedirectionRequest(preferredCommunication)
        return http.post(
          token,
          `${config.paymentEngineServiceUrl}/applications/${applicationId}/prepare_for_redirection/${queryParams}`,
          JSON.stringify(body),
        )
      }

    static signingStatus = (applicationId, token) => {
      return http.get(
        token,
        `${config.paymentEngineServiceUrl}/applications/${applicationId}/signing/status`,
      )
    }

    static updateSigningStatus = (signingInfo, token) => {
      return http.post(
        token,
        `${config.paymentEngineServiceUrl}/applications/${signingInfo.id}/signing`,
        JSON.stringify(signingInfo.statusObj),
      )
    }

    static skipSigning = (id, token) => {
      return http.post(
        token,
        `${config.paymentEngineServiceUrl}/applications/${id}/skip_signing`,
      )
    }

    static updateIdentificationStatus = (identificationInfo, token) => {
      return http.post(
        token,
        `${config.paymentEngineServiceUrl}/applications/${identificationInfo.id}/customer/identification`,
        JSON.stringify(identificationInfo.statusObj),
      )
    }

    static updateIdentificationStatusCoApp = (identificationInfo, token) => {
      return http.post(
        token,
        `${config.paymentEngineServiceUrl}/applications/${identificationInfo.id}/co_customer/identification`,
        JSON.stringify(identificationInfo.statusObj),
      )
    }

    static updateBusinessInformation = (applicationId, payload) => token => {
      return http.put(
        token,
        `${config.paymentEngineServiceUrl}/applications/${applicationId}/business`,
        JSON.stringify(payload),
      )
    }
  }

  static payment = class PaymentApi {
    static validateExternalId = (externalId, token) => {
      return http.get(
        token,
        `${config.paymentEngineServiceUrl}/payments/external_id/${externalId}/validate`,
      )
    }

    static search = (requestData, token) => {
      const queryParams = appendPageInfoQueryParams(requestData)
      const body = createSearchBody(requestData)

      return http.post(
        token,
        `${config.paymentEngineServiceUrl}/payments/search${queryParams}`,
        JSON.stringify(body),
        config.requestTimeoutTransactions,
      )
    }

    static get = (id, token) => {
      return http.get(
        token,
        `${config.paymentEngineServiceUrl}/payments/${id}`,
        config.requestTimeoutTransactions,
      )
    }

    static getCartSummary = (id, token) => {
      return http.get(
        token,
        `${config.paymentEngineServiceUrl}/payments/${id}/summary`,
        config.requestTimeoutTransactions,
      )
    }

    static addToCart = (cartRequest, token) => {
      return http.post(
        token,
        `${config.paymentEngineServiceUrl}/payments/${cartRequest.paymentId}/cart`,
        cartRequest.request,
        config.requestTimeoutTransactionAction,
      )
    }

    static replaceCart = (cartRequest, token) => {
      return http.put(
        token,
        `${config.paymentEngineServiceUrl}/payments/${cartRequest.paymentId}/cart`,
        cartRequest.request,
        config.requestTimeoutTransactionAction,
      )
    }

    static create = (payment, token) => {
      return http.post(
        token,
        `${config.paymentEngineServiceUrl}/payments`,
        payment,
      )
    }

    static authorize = (authRequest, token) => {
      return http.post(
        token,
        `${config.paymentEngineServiceUrl}/payments/${authRequest.paymentId}/authorize`,
        JSON.stringify(authRequest.authorize),
        config.requestTimeoutApply,
      )
    }

    static finishESigning = (paymentId, token) => {
      return http.post(
        token,
        `${config.paymentEngineServiceUrl}/payments/${paymentId}/finish_electronic_signing`,
      )
    }

    static debit = (debitRequest, token) => {
      return http.post(
        token,
        `${config.paymentEngineServiceUrl}/payments/${debitRequest.paymentId}/debit`,
        debitRequest.request,
        config.requestTimeoutTransactionAction,
      )
    }

    static credit = (creditRequest, token) => {
      return http.post(
        token,
        `${config.paymentEngineServiceUrl}/payments/${creditRequest.paymentId}/credit`,
        creditRequest.request,
        config.requestTimeoutTransactionAction,
      )
    }

    static annul = (annulRequest, token) => {
      return http.post(
        token,
        `${config.paymentEngineServiceUrl}/payments/${annulRequest.paymentId}/annul`,
        annulRequest.request,
        config.requestTimeoutTransactionAction,
      )
    }

    static cancel = paymentId => token =>
      http.put(
        token,
        `${config.paymentEngineServiceUrl}/payments/${paymentId}/cancel`,
        null,
        config.requestTimeoutTransactionAction,
      )

    static updateAutomaticDebitDate =
      (paymentId, automaticDebitDate) => token =>
        http.put(
          token,
          `${config.paymentEngineServiceUrl}/payments/${paymentId}/settings/automatic_debit_date`,
          JSON.stringify(automaticDebitDate),
        )

    static resendSigning =
      (
        paymentId,
        customerPreferredCommunications = {},
        coCustomerPreferredCommunications = {},
      ) =>
      token =>
        http.put(
          token,
          `${config.paymentEngineServiceUrl}/payments/${paymentId}/signing/resend`,
          JSON.stringify({
            customer: { ...customerPreferredCommunications },
            coCustomer: { ...coCustomerPreferredCommunications },
          }),
        )

    static prepareForRedirection =
      (
        paymentId,
        forceElectronicSigning,
        useDigitalAgreement = false,
        preferredCommunication = {},
        delayedPayment = false,
        automaticDebitDate = null,
      ) =>
      token => {
        let queryParams = ''
        queryParams = appendQueryParam(
          queryParams,
          'forceElectronicSigning',
          forceElectronicSigning,
        )
        queryParams = appendQueryParam(
          queryParams,
          'useDigitalAgreementSigning',
          useDigitalAgreement,
        )
        queryParams = appendQueryParam(
          queryParams,
          'delayedPayment',
          delayedPayment,
        )
        const communication =
          preferredCommunication.customer || preferredCommunication.coCustomer
            ? preferredCommunication
            : {}
        const body = {
          preferredCommunications: communication,
          sendLinks: true,
        }
        if (delayedPayment) {
          return http.post(
            token,
            `${config.paymentEngineServiceUrl}/payments/${paymentId}/delay`,
            JSON.stringify({
              ...body,
              ...(automaticDebitDate && { automaticDebitDate }),
            }),
          )
        }
        return http.post(
          token,
          `${config.paymentEngineServiceUrl}/payments/${paymentId}/prepare_for_redirection/${queryParams}`,
          JSON.stringify(body),
        )
      }

    static signingStatus = (paymentId, token) => {
      return http.get(
        token,
        `${config.paymentEngineServiceUrl}/payments/${paymentId}/application/signing/status`,
      )
    }

    static identificationStatus = (paymentId, token) => {
      return http.get(
        token,
        `${config.paymentEngineServiceUrl}/payments/${paymentId}/customer/identification/status`,
      )
    }

    static updateSigningStatus = (signingInfo, token) => {
      return http.post(
        token,
        `${config.paymentEngineServiceUrl}/payments/${signingInfo.id}/application/signing`,
        JSON.stringify(signingInfo.statusObj),
      )
    }

    static updateIdentificationStatus = (identificationInfo, token) => {
      return http.post(
        token,
        `${config.paymentEngineServiceUrl}/payments/${identificationInfo.id}/customer/identification`,
        JSON.stringify(identificationInfo.statusObj),
      )
    }

    static updateIdentificationStatusCoApp = (identificationInfo, token) => {
      return http.post(
        token,
        `${config.paymentEngineServiceUrl}/payments/${identificationInfo.id}/co_customer/identification`,
        JSON.stringify(identificationInfo.statusObj),
      )
    }
  }

  static getInvoicePdf = pdfId => token => {
    return http.getBlob(
      token,
      `${config.paymentEngineServiceUrl}/payments/invoices/${pdfId}`,
      'application/pdf',
    )
  }

  static resendInvoice = (paymentId, pdfId, email) => token => {
    return http.post(
      token,
      `${config.paymentEngineServiceUrl}/payments/${paymentId}/invoices/${pdfId}/resend`,
      JSON.stringify({ email }),
    )
  }

  static getPendingApplicationOnAccount =
    (governmentId, accountNumber) => token => {
      return http.get(
        token,
        `${config.paymentEngineServiceUrl}/applications/pending/government_id/${governmentId}/account/${accountNumber}`,
      )
    }

  static cancelSigning = applicationId => token =>
    http.put(
      token,
      `${config.paymentEngineServiceUrl}/applications/${applicationId}/signing/cancel`,
    )
}

export default PaymentEngineApi
